import { replayIntegration, init as sentryInit, tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { PostHogProvider } from "posthog-js/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "./components/ui/toaster";
import "./index.css";
import { config } from "./lib/config";
import { routeTree } from "./routeTree.gen";

const router = createRouter({
    routeTree,
    scrollRestoration: false,
});

// Initialize Sentry after router is created
if (config.sentry.dsn) {
    sentryInit({
        dsn: config.sentry.dsn,
        environment: config.sentry.environment,
        release: config.sentry.release,
        sampleRate: config.sentry.sampleRate,
        tracesSampleRate: config.sentry.tracesSampleRate,
        integrations: [
            tanstackRouterBrowserTracingIntegration(router),
            replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        beforeSend(event) {
            // Filter out PostHog events to avoid noise in Sentry
            if (event.exception?.values?.[0]?.value?.includes("PostHog")) {
                return null;
            }
            return event;
        },
        // Capture 10% of sessions for replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
    console.log("✅ Sentry initialized successfully");
    console.log(`   Environment: ${config.sentry.environment}`);
} else {
    console.warn("⚠️ Sentry disabled - VITE_SENTRY_DSN not set");
}

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <PostHogProvider
            apiKey={config.posthog.key || ""}
            options={{
                api_host: config.posthog.host,
                capture_exceptions: true,
                debug: config.posthog.debug,
                loaded: function (posthog) {
                    posthog.register({ environment: config.mode });

                    // Track app opened event for session analytics
                    if (!config.isDev) {
                        // Detect traffic source for user acquisition insights
                        const getTrafficSource = () => {
                            const referrer = document.referrer;
                            const currentUrl = window.location.href;

                            // Check if user came to a shared story URL
                            if (currentUrl.includes("/story/share/")) {
                                return "shared_link";
                            }

                            if (!referrer) {
                                return "direct";
                            } else if (referrer.includes("storydoodle.app")) {
                                return "internal";
                            } else {
                                return "referral";
                            }
                        };

                        posthog.capture("app_opened", {
                            timestamp: new Date().toISOString(),
                            user_agent: navigator.userAgent,
                            viewport_width: window.innerWidth,
                            viewport_height: window.innerHeight,
                            source: getTrafficSource(),
                            referrer: document.referrer || "none",
                        });
                    }

                    // Check for cookie consent before enabling analytics
                    const hasConsent = (window as Window & { posthogConsentGiven?: boolean }).posthogConsentGiven;

                    // Disable PostHog in development to prevent noise
                    if (config.isDev) {
                        posthog.opt_out_capturing();
                        posthog.set_config({ disable_session_recording: true });
                    } else if (hasConsent === false) {
                        // User explicitly rejected analytics cookies
                        posthog.opt_out_capturing();
                    }
                    // If hasConsent is true or undefined, PostHog will track normally
                },
            }}
        >
            <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
                <Toaster />
            </QueryClientProvider>
        </PostHogProvider>
    </StrictMode>
);
